import { z } from 'zod';
import { localeSchema } from './common.js';
import { absoluteNutrientsSchema, nutritionBasisSchema, nutrientsSchema } from './nutrients.js';
import { foodSchema, servingSchema } from './food.js';
import { quantitySchema } from './diary.js';
// ---------------------------------------------------------------------------
// Free-text parsing
// ---------------------------------------------------------------------------
export const parseTextSchema = z.object({
    /** e.g. "300g hühnerbrust 100g frischkäse, 200g reis" */
    text: z.string().trim().min(1).max(1000),
    locale: localeSchema.optional(),
    /**
     * Allow the LLM to estimate nutrients for items no catalog can resolve.
     * Off by default: a guessed value should be an explicit choice.
     */
    allowEstimates: z.boolean().default(false),
});
/** How a parsed item was matched to a food, ordered by decreasing trust. */
export const matchSourceSchema = z.enum([
    'user_food',
    'seed',
    'catalog',
    'openfoodfacts',
    'llm_estimate',
]);
export const foodMatchSchema = z.object({
    food: foodSchema,
    matchedVia: matchSourceSchema,
    /** 0..1. Anything below ~0.6 is shown to the user as needing confirmation. */
    confidence: z.number().min(0).max(1),
});
/**
 * One line item the parser recovered. Always returned as a *draft*: the PWA
 * shows it for confirmation and only then posts it to the diary. Nothing is
 * written to the diary as a side effect of parsing.
 */
export const parsedItemSchema = z.object({
    /** The exact slice of input this item came from. */
    raw: z.string(),
    /** The food name with quantity words stripped, e.g. "hühnerbrust". */
    query: z.string(),
    /** Null when no amount was stated, in which case the UI asks for one. */
    quantity: quantitySchema.nullable(),
    /** Best match, or null when nothing resolved. */
    match: foodMatchSchema.nullable(),
    /** Runner-up matches, for the "not this one?" picker. */
    candidates: z.array(foodMatchSchema).max(10),
});
export const parseResultSchema = z.object({
    items: z.array(parsedItemSchema),
    /** Count of items with no match — surfaced as a warning banner. */
    unresolvedCount: z.number().int(),
});
// ---------------------------------------------------------------------------
// Dish estimation
// ---------------------------------------------------------------------------
export const estimateDishSchema = z.object({
    /** A dish by name, with no amounts: "chili con carne". */
    name: z.string().trim().min(2).max(120),
    /** Portions the estimate should describe. Defaults to a single serving. */
    servings: z.number().int().min(1).max(20).default(1),
    locale: localeSchema.optional(),
});
/**
 * One ingredient the model proposed for a dish.
 *
 * The *amount* is the model's guess, but the nutrients come from whatever the
 * ingredient resolved to in the catalog — so the numbers are measured values at
 * a guessed weight, rather than a recollection of the finished dish.
 */
export const dishComponentSchema = z.object({
    /** What the model named, e.g. "Rinderhackfleisch". */
    query: z.string(),
    /** The model's estimated weight for the whole dish, in grams. */
    grams: z.number().min(0),
    /** Null when nothing in the catalog matched; then it contributes nothing. */
    match: foodMatchSchema.nullable(),
    /** Nutrients contributed by this ingredient at `grams`. Absolute, so it is
     *  not bound by the per-100 caps. */
    totals: absoluteNutrientsSchema,
});
export const dishEstimateSchema = z.object({
    /** The model's canonical name for the dish. */
    name: z.string(),
    servings: z.number().int(),
    components: z.array(dishComponentSchema),
    /** Summed weight of the resolved ingredients, in grams. */
    totalGrams: z.number(),
    /** Absolute nutrients for the whole dish. */
    totals: absoluteNutrientsSchema,
    /** Derived per-100 g values, so the dish logs like any other food. */
    per100: nutrientsSchema,
    /** Ingredients the catalog could not resolve; they are excluded from totals. */
    unresolvedCount: z.number().int(),
});
// ---------------------------------------------------------------------------
// Nutrition-label photo scanning
// ---------------------------------------------------------------------------
/**
 * What the vision model is asked to return. Deliberately narrow: the model
 * transcribes a table, it does not reason about the food.
 *
 * `column` records which column of a two-column European label was read.
 * Labels routinely print "per 100 g" beside "per portion"; only the former is
 * usable as a food's basis, so reading the wrong one must be detectable.
 */
export const labelExtractionSchema = z.object({
    productName: z.string().trim().max(200).nullable().default(null),
    brand: z.string().trim().max(120).nullable().default(null),
    basis: nutritionBasisSchema.default('per_100g'),
    column: z.enum(['per_100', 'per_serving', 'unknown']).default('per_100'),
    nutrients: nutrientsSchema,
    serving: servingSchema.nullable().default(null),
    /** Energy in kJ as printed, kept so the kcal value can be cross-checked. */
    energyKj: z.number().min(0).max(4200).nullable().default(null),
    /**
     * Where the kcal figure came from.
     *
     * EU labels print both units, but plenty of others print kJ alone. Rather
     * than record zero calories for a food that plainly has some, the kcal is
     * computed from the kJ — and this says so, because a derived figure is one
     * statement about energy where a printed pair is two, and only the pair can
     * be checked against itself.
     */
    kcalSource: z.enum(['label', 'derived_from_kj']).default('label'),
});
export const labelScanResultSchema = z.object({
    extraction: labelExtractionSchema,
    /** 0..1, derived from internal consistency checks rather than model opinion. */
    confidence: z.number().min(0).max(1),
    /** Human-readable notes, e.g. "kcal disagreed with kJ by 12%". */
    warnings: z.array(z.string()),
    /** Which model produced this, for debugging. */
    model: z.string(),
    durationMs: z.number().int().min(0),
});
// ---------------------------------------------------------------------------
// Meal-photo identification
// ---------------------------------------------------------------------------
/**
 * What the model is asked for when shown a plate of food.
 *
 * Deliberately *only* names. Measuring a portion from a photo was tested and
 * does not work: weight came out 197% high on a reference item, and repeated
 * runs on one unchanged image ranged 250-650 kcal. Worse, the numbers are
 * internally consistent, so the cross-checks that catch a misread label cannot
 * catch this — a plate never states its own weight, so there is nothing to
 * check against.
 *
 * Identification alone is markedly better, and asking for nothing else visibly
 * improves it: the same model that called a Big Mac a "Double Cheeseburger"
 * while estimating portions named it exactly, on every run, once the portion
 * question was removed. So the model names the dish, the catalog supplies
 * per-100 g values that were actually measured, and the user supplies the
 * weight.
 */
export const photoIdentificationSchema = z.object({
    isFood: z.boolean(),
    /** Common German name, phrased as one would search a food database. */
    dishName: z.string().trim().max(200).nullable().default(null),
    /** Other plausible names, because the top guess is not always right. */
    alternatives: z.array(z.string().trim().max(200)).max(6).default([]),
    /** Visible ingredients, usable as search terms when the dish is unknown. */
    components: z.array(z.string().trim().max(120)).max(12).default([]),
    confidence: z.number().min(0).max(1).default(0),
});
/**
 * A candidate food for the photo, resolved against the catalog.
 *
 * `term` records which of the model's guesses produced it, so the UI can show
 * why a suggestion is being offered.
 */
export const photoCandidateSchema = z.object({
    term: z.string(),
    match: foodMatchSchema,
});
export const photoScanResultSchema = z.object({
    identification: photoIdentificationSchema,
    /**
     * Ranked catalog hits for the dish name, its alternatives and the visible
     * components. Every guess is resolved, not just the top one — a wrong
     * headline guess usually still leaves the right food one tap away.
     */
    candidates: z.array(photoCandidateSchema),
    model: z.string(),
    durationMs: z.number().int().min(0),
});
/** Optional hints posted alongside the image in the multipart body. */
export const labelScanHintsSchema = z.object({
    barcode: z.string().trim().max(20).optional(),
    productName: z.string().trim().max(200).optional(),
});
//# sourceMappingURL=intake.js.map